// ═══════════════════════════════════════════════════════════════════════════
// useThisDevice — real details for the machine the dashboard is running on.
//
// Sources (all live, nothing invented):
//   • navigator            → platform / device kind
//   • daemon GET /health   → app version  (proves the app is running)
//   • daemon GET /whoami   → signed-in account
//   • DaemonContext        → app status + tunnel state
//
// Scope note: the daemon is loopback-only, so it can only ever describe THIS
// machine. Listing a user's other devices needs an enterprise-backend endpoint
// (enterpriseDeviceService.findActiveByUser exists server-side but is not
// exposed over HTTP yet) — see ProtectedDevicesPanel's empty state.
// ═══════════════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import { useDaemon } from "../../context/DaemonContext";
import { getDaemonHealth, getDaemonUser } from "../../services/daemonService";

/** Best-effort platform label from the browser. */
function detectPlatform() {
  const ua = navigator.userAgent || "";
  const uaPlatform = navigator.userAgentData?.platform;

  if (uaPlatform) return uaPlatform;                 // Chromium: "macOS", "Windows"…
  if (/Windows/i.test(ua)) return "Windows";
  if (/Macintosh|Mac OS X/i.test(ua)) return "macOS";
  if (/Android/i.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
  if (/Linux/i.test(ua)) return "Linux";
  return "Unknown";
}

/** Phone/tablet vs computer — picks the card icon. */
function detectKind() {
  const ua = navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua) ? "mobile" : "desktop";
}

export default function useThisDevice() {
  const { daemonStatus, vpnStatus } = useDaemon();
  const [detail, setDetail] = useState({ version: null, email: null });

  // Pull the extra detail the context doesn't carry, once the app is reachable.
  useEffect(() => {
    if (daemonStatus !== "ready" && daemonStatus !== "not-authenticated") {
      setDetail({ version: null, email: null });
      return undefined;
    }

    let cancelled = false;
    Promise.allSettled([getDaemonHealth(), getDaemonUser()]).then(([health, who]) => {
      if (cancelled) return;
      setDetail({
        version: health.status === "fulfilled" ? health.value?.version ?? null : null,
        email: who.status === "fulfilled" && who.value?.loggedIn ? who.value.email ?? null : null,
      });
    });

    return () => { cancelled = true; };
  }, [daemonStatus]);

  const running = daemonStatus === "ready" || daemonStatus === "not-authenticated";
  const platform = detectPlatform();
  const kind = detectKind();

  return {
    id: "this-device",
    name: "This device",
    kind,
    thisDevice: true,
    platform,
    version: detail.version,
    email: detail.email,
    // "Just now" is honest here: the card only renders while the local probe
    // is succeeding, so the app was seen within the last probe interval.
    lastSeen: running ? "Just now" : "—",
    appStatus: daemonStatus === "ready" ? "Open" : running ? "Signed out" : "Closed",
    statusLabel: daemonStatus === "ready" ? "APP OPENED" : "APP NOT SIGNED IN",
    tunnelState: vpnStatus?.state ?? "disconnected",
    description: detail.email
      ? `Signed in as ${detail.email} — protected by your organization.`
      : "The PQ VPN app is running on this machine but no account is signed in.",
  };
}
